import { HttpErrorResponse } from '@angular/common/http';

/**
 * The outcome of the login authorization check.
 *
 * THREE outcomes, not two. The previous code collapsed everything into
 * `catchError(() => of(null))`, so a 401, a 500, a timeout, a CORS failure and a
 * genuine "no such user" were indistinguishable — every one produced
 * /unauthorized AND cached roles=[], latching a valid user out of the app until
 * they cleared storage. A transient blip must never be able to do that.
 */
export type AuthorizationResult =
  | { kind: 'authorized'; user: unknown }
  /** The server answered, and the answer is no. */
  | { kind: 'denied'; status: number }
  /** We never got an answer. Says nothing about entitlement. */
  | { kind: 'unavailable'; status: number; message: string };

/**
 * Which HTTP statuses mean "the server decided", and which mean "ask again".
 *
 * 403 and 404 are decisions: the identity was understood and refused, or has no
 * record. Everything else — 0 (network/CORS), 408, 429, and every 5xx — is the
 * server failing to answer, which is not evidence about the user.
 *
 * 401 is deliberately TRANSIENT here. It means the bearer was missing or expired
 * at that moment, which after a reload is far more often a token not yet acquired
 * than a user who may not enter. Treating it as a denial is precisely how a valid
 * session got latched out.
 */
export function classifyAuthorizationError(
  error: unknown
): { kind: 'denied'; status: number } | { kind: 'unavailable'; status: number; message: string } {
  const http = error as HttpErrorResponse;
  const status = typeof http?.status === 'number' ? http.status : 0;
  if (status === 403 || status === 404) {
    return { kind: 'denied', status };
  }
  return {
    kind: 'unavailable',
    status,
    message: http?.message ?? String(error ?? 'unknown'),
  };
}

/** Retry only what is worth retrying, and not for long enough to hang a login. */
export const AUTH_RETRY_ATTEMPTS = 3;
export const AUTH_RETRY_BASE_MS = 400;

/** Exponential backoff: 400ms, 800ms, 1600ms. */
export function authRetryDelayMs(attempt: number): number {
  return AUTH_RETRY_BASE_MS * Math.pow(2, Math.max(0, attempt - 1));
}
